<template>
  <section>
    <PageTitle guest>
      <Logo />
    </PageTitle>
    <PageContent>
      <form @submit.prevent="submit" v-if="!sendMail">
        <Container>
          <Headline :level="2">ID確認</Headline>
          <div class="form-group">
            <p class="text-paragraph text-xs">
              メールアドレスを入力してください。<br class="tablet-only" />新しいパスワードを作成するためのコードをメールでお送り致します。
            </p>
          </div>
          <NoticeBox type="error" v-if="hasErrors">
            {{ invalidMessage }}
          </NoticeBox>
          <div class="form-group">
            <label for="email">メールアドレス</label>
            <input
              id="email"
              type="text"
              class="form-control"
              :class="{ 'has-error': hasError('email') }"
              v-model="email"
            />
            <FormError v-if="errors.email" :errors="errors.email" />
            <p class="form-memo text-right">
              <router-link :to="backLink">
                ログイン画面へ戻る
              </router-link>
            </p>
          </div>
          <div class="form-footer text-center">
            <WaveButton type="submit" :loading="loading">
              次へ進む
            </WaveButton>
          </div>
        </Container>
      </form>
      <form @submit.prevent="change" v-if="sendMail">
        <Container>
          <Headline :level="2">パスワードのリセット</Headline>
          <div class="form-group">
            <p class="text-paragraph text-xs">
              あなたのメールアドレス宛てに、コードを送信しました。<br class="tablet-only" />
              この画面は閉じずに、メール内に記載された確認コードをコピー&ペーストの上、<br class="tablet-only" />
              新しいパスワードを入力して、「送信する」ボタンを押してください。
            </p>
          </div>
          <NoticeBox type="info" v-if="resend">
            確認コードを送信しました。
          </NoticeBox>
          <NoticeBox type="error" v-if="passwordResetError">
            {{ passwordResetErrorMessage }}
          </NoticeBox>
          <NoticeBox type="error" v-if="hasErrors">
            {{ invalidMessage }}
          </NoticeBox>
          <div class="form-group">
            <label for="email">メールアドレス</label>
            <input
              id="email"
              type="text"
              class="form-control"
              :class="{ 'has-error': hasError('email') || passwordResetError }"
              v-model="email"
            />
            <FormError v-if="errors.email" :errors="errors.email" />
          </div>
          <div class="form-group">
            <label for="code">確認コード</label>
            <input
              id="code"
              type="text"
              class="form-control"
              :class="{ 'has-error': hasError('code') || passwordResetError }"
              v-model="code"
            />
            <FormError v-if="errors.code" :errors="errors.code" />
            <p class="form-memo text-right">
              <a href="#" @click.prevent="resendCode">
                コードを再送
              </a>
            </p>
          </div>
          <div class="form-group">
            <label for="password">新しいパスワード</label>
            <input
              id="password"
              type="password"
              class="form-control"
              :class="{ 'has-error': hasError('password') }"
              v-model="password"
            />
            <FormError v-if="errors.password" :errors="errors.password" />
            <p class="form-memo rule">8文字以上、半角英小文字と数字を含む</p>
          </div>
          <div class="form-footer text-center">
            <WaveButton type="submit" :loading="loading" :disabled="disabled">
              送信する
            </WaveButton>
          </div>
        </Container>
      </form>
    </PageContent>
    <Modal
      id="reset-pass"
      title="パスワードの変更が完了しました"
      icon="check"
      @ok="ok"
    />
  </section>
</template>

<script>
import { Auth } from 'aws-amplify'
import Modal from '@/components/Modal.vue'
import NoticeBox from '@/components/NoticeBox.vue'
import FormError from '@/components/FormError.vue'
import validationMixin from '@/mixins/validation'
import {
  PASSWORD_RESET_FAILED,
  CODE_MISMATCH,
  CODE_EXPIRED,
  USER_NOT_FOUND,
  PASSWORD_INVALID } from '@/messages/errors'
import { renderError } from '@/utils/error'

export default {
  components: {
    Modal,
    FormError,
    NoticeBox
  },
  rules: {
    email: ['メールアドレス', ['required']],
    code: ['コード', ['required']],
    password: ['パスワード', ['required', 'password']],
  },
  navbar: false,
  metaInfo() {
    return {
      title: this.sendMail ? 'パスワード変更' : 'パスワードを忘れた方はこちら',
      bodyAttrs: { class: 'login' }
    }
  },
  data() {
    return {
      email: '',
      code: '',
      password: '',
      sendMail: false,
      resend: false,
      loading: false,
      disabled: false,
      backLink: '',
      passwordResetError: false,
      passwordResetErrorMessage: PASSWORD_RESET_FAILED,
    }
  },
  methods: {
    async submit() {
      if (this.loading) {
        return false
      }

      if (!this.validate(['email'])) {
        window.scrollTo(0, 0)
        return false
      }

      this.loading = true

      try {
        this.email = this.email.trim()
        await Auth.forgotPassword(this.email)
        this.sendMail = true
      } catch(err) {
        if (err.code === 'UserNotFoundException') {
          this.addValidateError('email', USER_NOT_FOUND)
          return window.scrollTo(0, 0)
        } else if (err.code.includes("Exception")) {
          // メールアドレス形式エラー等、その他のCognitoエラーの場合
          this.addValidateError('email', USER_NOT_FOUND)
          return window.scrollTo(0, 0)
        }
        renderError(err)
      } finally {
        this.loading = false
      }
    },
    async change() {
      if (this.loading) {
        return false
      }

      this.passwordResetError = false

      this.resend = false

      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }

      this.loading = true

      try {
        this.email = this.email.trim()
        this.code = this.code.trim()
        this.password = this.password.trim()
        await Auth.forgotPasswordSubmit(
          this.email,
          this.code,
          this.password
        )
        this.disabled = true
        this.$modal.show('reset-pass')
      } catch(err) {
        if (err.code === 'CodeMismatchException') {
          // 確認コードが間違っているとき
          this.addValidateError('code', CODE_MISMATCH)
        } else if (err.code === 'ExpiredCodeException') {
          // 確認コード有効期限切れ
          this.addValidateError('code', CODE_EXPIRED)
        } else if (err.code === 'InvalidPasswordException') {
          // パスワード形式エラー
          this.addValidateError('password', PASSWORD_INVALID)
        } else if (err.code === 'UserNotFoundException') {
          // ユーザー非存在
          this.addValidateError('email', USER_NOT_FOUND)
        } else if (err.code.includes("Exception")) {
          // メールアドレス形式エラー等、その他のCognitoエラーの場合
          this.passwordResetError = true
        } else {
          renderError(err)
        }

        window.scrollTo(0, 0)
      } finally {
        this.loading = false
      }
    },
    ok() {
      this.$wcRouter.push({ name: 'auth.login' })
    },
    async resendCode() {
      if (!this.validate(['email'])) {
        window.scrollTo(0, 0)
        return false
      }

      this.passwordResetError = false

      this.code = ''
      this.resend = false

      try {
        this.email = this.email.trim()
        await Auth.forgotPassword(this.email)
        this.resend = true
      } catch(err) {
        if (err.code === 'UserNotFoundException') {
          this.addValidateError('email', USER_NOT_FOUND)
          return window.scrollTo(0, 0)
        } else if (err.code.includes("Exception")) {
          // メールアドレス形式エラー等、その他のCognitoエラーの場合
          this.addValidateError('email', USER_NOT_FOUND)
          return window.scrollTo(0, 0)
        }
        renderError(err)
      }
    }
  },
  mixins: [validationMixin],
  beforeRouteEnter(to, from, next) {
    // 遷移元から戻り先URLを判別する
    // 外部連携用ログイン画面から来たときは外部連携用ログイン画面に戻る
    next(vm => {
      vm.backLink = from.name === 'oauth2.login'
        ? from.fullPath
        : vm.$wcRouter.path({ name: 'auth.login' })
    })
  }
}
</script>
